import { Skeleton } from "@/components/ui/skeleton";
import { cx } from "@/utils/cx";

const LIST_ROW_COUNT = 8;

// The detail panel's own skeleton — reused by FlaggedTabsLoadingSkeleton below (whole-tab
// loading) and standalone when only the selected video's detail is still loading (e.g. a
// deep-linked video not yet on a loaded page — see TabWithCreator).
export const DetailPanelLoadingSkeleton = ({ className }: { className: string }) => (
  <div className={cx("border border-border-primary rounded-md bg-white overflow-hidden flex flex-col", className)}>
    <div className="flex items-center gap-3.5 p-4">
      <Skeleton className="w-12 h-12 rounded-sm shrink-0" />
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <Skeleton className="h-4 w-1/2 rounded" />
        <Skeleton className="h-3 w-1/3 rounded" />
      </div>
    </div>
    <div className="flex-1 p-4 flex flex-col gap-4">
      <Skeleton className="h-32 w-full rounded-md" />
      <Skeleton className="h-24 w-full rounded-md" />
      <Skeleton className="h-24 w-full rounded-md" />
    </div>
  </div>
);

type FlaggedTabsLoadingSkeletonProps = {
  gapClassName: string;
  listClassName: string;
  detailClassName: string;
};

// Mirrors the real two-column layout (video list + detail panel) while a tab's videos are
// loading, instead of a plain "Loading videos..." message. `listClassName`/`detailClassName`
// take each tab's own width/flex + height classes so the skeleton matches exactly and nothing
// resizes once the real content lands.
export const FlaggedTabsLoadingSkeleton = ({
  gapClassName,
  listClassName,
  detailClassName,
}: FlaggedTabsLoadingSkeletonProps) => (
  <div className={cx("flex flex-row w-[100%] flex-1 min-h-0", gapClassName)}>
    <div
      className={cx("border border-border-primary rounded-md bg-white overflow-hidden flex flex-col", listClassName)}
    >
      {Array.from({ length: LIST_ROW_COUNT }, (_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 border-b border-border-secondary last:border-b-0">
          <Skeleton className="w-10 h-10 rounded-md shrink-0" />
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <Skeleton className="h-3.5 w-3/4 rounded" />
            <Skeleton className="h-3 w-1/2 rounded" />
          </div>
        </div>
      ))}
    </div>
    <DetailPanelLoadingSkeleton className={detailClassName} />
  </div>
);
